/// <reference types="vite/client" />
// ─────────────────────────────────────────────────────
// Analytics Page — Wardrobe insights, wear frequency, style trends
// ─────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";
import { api } from "../services/api";

const wearData = [
  { name: "Mon", wears: 4 },
  { name: "Tue", wears: 7 },
  { name: "Wed", wears: 5 },
  { name: "Thu", wears: 8 },
  { name: "Fri", wears: 12 },
  { name: "Sat", wears: 15 },
  { name: "Sun", wears: 6 },
];


const styleScoreData = [
  { week: "W1", score: 82 },
  { week: "W2", score: 85 },
  { week: "W3", score: 84 },
  { week: "W4", score: 89 },
  { week: "W5", score: 91 },
  { week: "W6", score: 88 },
  { week: "W7", score: 93 },
  { week: "W8", score: 94 },
];

const tryOnData = [
  { day: "Mon", tryOns: 8, saved: 3 },
  { day: "Tue", tryOns: 12, saved: 5 },
  { day: "Wed", tryOns: 6, saved: 2 },
  { day: "Thu", tryOns: 15, saved: 7 },
  { day: "Fri", tryOns: 22, saved: 10 },
  { day: "Sat", tryOns: 28, saved: 14 },
  { day: "Sun", tryOns: 10, saved: 4 },
];

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-sxt-surface rounded-2xl p-6 border animate-fade-in">
      <h3 className="text-lg font-bold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function InsightCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-sxt-surface rounded-2xl p-5 border animate-fade-in">
      <p className="text-xs text-sxt-muted uppercase tracking-wider mb-2">{label}</p>
      <p className="text-2xl font-bold text-sxt-primary">{value}</p>
      <p className="text-xs text-sxt-dim mt-1">{sub}</p>
    </div>
  );
}

export function AnalyticsPage() {
  const [categoryData, setCategoryData] = useState([
    { name: "Tops", value: 0, color: "#6C63FF" },
    { name: "Bottoms", value: 0, color: "#00E676" },
    { name: "Outerwear", value: 0, color: "#FFB74D" },
    { name: "Shoes", value: 0, color: "#FF5252" },
    { name: "Dresses", value: 0, color: "#E040FB" },
    { name: "Accessories", value: 0, color: "#00BCD4" },
  ]);
  const [_totalItems, setTotalItems] = useState(0);
  const [_loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAnalytics() {
      try {
        const wardrobe = await api.getWardrobe();
        const items = Array.isArray(wardrobe) ? wardrobe : [];
        setTotalItems(items.length);
        // Build category counts from real wardrobe
        const counts: Record<string, number> = {};
        items.forEach((item: Record<string, string>) => {
          const cat = item.category || "Other";
          counts[cat] = (counts[cat] || 0) + 1;
        });
        const colorMap: Record<string, string> = {
          Tops: "#6C63FF", Bottoms: "#00E676", Outerwear: "#FFB74D",
          Shoes: "#FF5252", Dresses: "#E040FB", Accessories: "#00BCD4",
        };
        const catArr = Object.entries(counts).map(([name, value]) => ({
          name, value, color: colorMap[name] || "#888",
        }));
        if (catArr.length) setCategoryData(catArr);
      } catch { /* graceful fallback */ }
      setLoading(false);
    }
    fetchAnalytics();
  }, []);
  return (
    <div>
      <h2 className="text-3xl font-bold mb-8">Analytics</h2>

      {/* Top Insights */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <InsightCard label="Most Worn" value="Chelsea Boots" sub="30 wears this month" />
        <InsightCard label="Best Match" value="96%" sub="Navy Blazer + White Oxford" />
        <InsightCard label="Unused Items" value="5" sub="Not worn in 30+ days" />
        <InsightCard label="Cost Per Wear" value="$2.41" sub="Average across wardrobe" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <ChartCard title="Weekly Wear Frequency">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={wearData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="name" stroke="#888" fontSize={12} />
              <YAxis stroke="#888" fontSize={12} />
              <Tooltip
                contentStyle={{ backgroundColor: "#151515", border: "1px solid #222", borderRadius: 12 }}
                labelStyle={{ color: "#FFF" }}
              />
              <Bar dataKey="wears" fill="#6C63FF" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Wardrobe by Category">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                dataKey="value"
                stroke="none"
              >
                {categoryData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: "#151515", border: "1px solid #222", borderRadius: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-2 justify-center">
            {categoryData.map((c) => (
              <div key={c.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                <span className="text-xs text-sxt-muted">{c.name} ({c.value})</span>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Style Match Score Trend">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={styleScoreData}>
              <defs>
                <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6C63FF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6C63FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="week" stroke="#888" fontSize={12} />
              <YAxis stroke="#888" fontSize={12} domain={[75, 100]} />
              <Tooltip
                contentStyle={{ backgroundColor: "#151515", border: "1px solid #222", borderRadius: 12 }}
              />
              <Area type="monotone" dataKey="score" stroke="#6C63FF" fill="url(#scoreGradient)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Try-On Activity">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={tryOnData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="day" stroke="#888" fontSize={12} />
              <YAxis stroke="#888" fontSize={12} />
              <Tooltip
                contentStyle={{ backgroundColor: "#151515", border: "1px solid #222", borderRadius: 12 }}
              />
              <Line type="monotone" dataKey="tryOns" stroke="#6C63FF" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="saved" stroke="#00E676" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex gap-6 mt-2 justify-center">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-sxt-primary" />
              <span className="text-xs text-sxt-muted">Try-Ons</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-sxt-accent" />
              <span className="text-xs text-sxt-muted">Saved</span>
            </div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
